import { createContext, useContext, ReactNode } from 'react';
import { useTwilioVoice } from '@hooks/useTwilioVoice';
import { CallState, TwilioDeviceState, CallEntityType } from '@/types/twilio';
import { useStore } from '@hooks/useStore';
import { CallNotification } from 'stubben-edge-unified-frontend-react-components';

interface TwilioContextType {
  deviceState: TwilioDeviceState;
  callState: CallState;
  initiateCall: (
    entityId: string,
    phoneNumber: string,
    callerName: string,
    entityType: CallEntityType
  ) => Promise<void>;
  terminateCall: () => void;
  getCallStatusText: () => string;
  canMakePhoneCalls: boolean;
}

const TwilioContext = createContext<TwilioContextType | undefined>(undefined);

export function useTwilio() {
  const context = useContext(TwilioContext);
  if (context === undefined) {
    throw new Error('useTwilio must be used within a TwilioProvider');
  }
  return context;
}

interface TwilioProviderProps {
  children: ReactNode;
}

export function TwilioProvider({ children }: TwilioProviderProps) {
  const {
    deviceState,
    callState,
    initiateCall,
    terminateCall,
    getCallStatusText
  } = useTwilioVoice();
  const { authStore } = useStore();
  const { user } = authStore;

  return (
    <TwilioContext.Provider
      value={{
        deviceState,
        callState,
        initiateCall,
        terminateCall,
        getCallStatusText,
        canMakePhoneCalls: user?.canMakePhoneCalls ?? false
      }}
    >
      {children}
      {callState.status !== 'idle' && callState.status !== 'disconnected' && (
        <span className="fixed bottom-0 z-50 sm:left-[256px]">
          <div className="mb-36  h-[112px] w-[256px] pl-0  md:pl-10 lg:pl-10">
            <CallNotification
              callType={
                callState.status === 'connected' ? 'connected' : 'outgoing'
              }
              onHangUp={terminateCall}
              phoneNumber={callState.callerName || ''}
              status={getCallStatusText()}
              error={callState.error || ''}
            />
          </div>
        </span>
      )}
    </TwilioContext.Provider>
  );
}
